import PropTypes from 'prop-types'
import React, { useState, useRef, useEffect, useContext } from 'react'
import styled, { theme, prop } from '@nobia/zeus-components/lib/styled'

import FinanceSelection from '../financeCalculator/FinanceSelection'
import Email from '../email/Email'
import {
  getFormatedPrice,
  formatPrice,
  getInstallationText,
} from '../cards/utils'
import {
  getMonthlyPayment,
  getMonthlyPaymentShortVersion,
  hasMonthlyPayment,
  hasFullVersion,
  hasMagnetSimplifiedVersion,
} from '../../brands/monthsToPay'
import { useTranslation } from '../../utils/useTranslation'
import Context from '../../app/context'

const Wrapper = styled.div`
  padding: 40px ${prop('theme.body.padding')};
  margin: 0;
  @media (min-width: ${prop('theme.body.desktopWidth')}) {
    margin: 0 ${prop('theme.body.marginDesktop')};
  }
  @media (min-width: ${prop('theme.body.desktopWidth')}) {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }
`
const SectionSummary = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 15px;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    flex-direction: row;
    align-items: center;
  }
`
const Row = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  font-family: ${prop('theme.font.paragraph.fontFamily')};
  .custom-select-container {
    display: flex;
    width: 75%;
    min-width: 200px;
    max-width: 400px;
    justify-content: space-between;
    align-items: center;
    padding: 0;
    margin: 0 10px 0 0;
    @media (min-width: ${prop('theme.body.desktopWidth')}) {
      width: 60%;
      margin: 0 20px;
    }
    @media (min-width: 1200px) {
      width: 80%;
    }
    span {
      display: none;
    }
    select {
      opacity: 1;
    }
  }
`

const Select = styled.select`
  background-image: ${({ backgroundUrl }) => `url(${backgroundUrl})`};
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-repeat: no-repeat, repeat;
  background-position: right 6px top 50%, 0 0;
  background-size: 1.66em auto, 100%;
  padding: 10px;
  font-size: 16px;
  font-family: ${prop('theme.font.select.fontFamily')};
  font-weight: ${prop('theme.font.select.fontWeight')};
  border-radius: 3px;
  border: 1px solid ${prop('theme.input.borderColor')};
  min-width: 220px;
  margin: 0;
  max-width: 400px;
  background-color: ${prop('theme.input.select.bgColor')};
  color: ${theme('colors.primary')};
  width: 75%;
  margin: 0 10px 0 0;
  opacity: 1;
  cursor: pointer;
  @media (min-width: ${prop('theme.body.desktopWidth')}) {
    width: 60%;
    margin: 0 20px;
  }
  @media (min-width: ${prop('theme.body.tabletWidth')}) and (max-width: ${prop(
      'theme.body.desktopWidth'
    )}) {
    margin-left: ${prop('theme.input.mobile.marginLeft')};
  }
`

const Label = styled.label`
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  width: 100px;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    text-align: ${prop('theme.selection.textAlign')};
    padding-right: ${prop('theme.summary.paddingRight')};
  }
  @media (min-width: ${prop('theme.body.desktopWidth')}) {
    text-align: right;
    padding-right: ${prop('theme.summary.mobile.paddingRight')};
  }
`
const CenteredLabel = styled.label`
  margin-bottom: 4px;
  font-size: 16px;
  font-weight: 600;
  text-align: left;
  padding-left: 0px;
  width: 100px;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    padding-left: 90px;
    text-align: left;
    padding-right: ${prop('theme.summary.paddingRight')};
  }
  @media (min-width: ${prop('theme.body.desktopWidth')}) {
    padding-left: 105px;
    text-align: left;
    padding-right: ${prop('theme.summary.mobile.paddingRight')};
  }
`

const Price = styled.strong`
  font-size: 16px;
  font-family: ${prop('theme.font.preamble.fontFamily')};
  font-weight: ${prop('theme.font.strong.fontWeight')};
`
const SectionSummaries = styled.div`
  max-width: 600px;
  margin: 0 auto;
  margin-bottom: 40px;
  @media (min-width: ${prop('theme.body.desktopWidth')}) {
    margin: 40px ${prop('theme.body.marginDesktop')} 40px
      ${prop('theme.body.marginDesktop')};
    width: 100%;
  }
`
const CostWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
`

const CostRow = styled(Row)`
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    margin: 0 0px 0px 90px;
  }
  @media (min-width: ${prop('theme.body.desktopWidth')}) {
    margin: 0 0px 0px 105px;
  }
  @media (min-width: 1200px) {
    margin: 0 0px 0px 107px;
  }
`

const Span = styled.span`
  display: flex;
  justify-content: space-between;
  max-width: 400px;
  min-width: 200px;
  width: 75%;
  padding: ${(p) =>
    p.hasMonthlyPayment && p.hasFullVersion && !p.hasMagnetSimplifiedVersion
      ? '0 0 28px 0'
      : '0'};
  border-bottom: ${(p) =>
    p.hasMonthlyPayment && p.hasFullVersion && !p.hasMagnetSimplifiedVersion
      ? '1px solid rgba(25,34,40, 0.1)'
      : 'none'};
  width: ${(p) => `${p.elementWidth}px`};
`

const P = styled.p`
  display: flex;
  justify-content: space-between;
  text-align: left;
  margin: 0;
  font-size: ${prop('theme.costSummary.totalCost.fontSize')};
`

const Cost = styled(P)`
  margin: 0;
  display: block;
`
const Total = styled(P)`
  margin: 0;
  display: block;
  font-weight: 600;
`

const Strong = styled.strong`
  font-size: ${prop('theme.costSummary.totalCost.fontSize')};
  font-weight: 700;
  padding-left: 10px;
  display: block;
  text-align: right;
  padding-right: 18px;
`
const StrongSummery = styled.strong`
  font-size: ${prop('theme.costSummary.totalCost.fontSize')};
  font-weight: 500;
  padding-left: 105px;
  width: 330px;
  text-align: left;
  @media (max-width: ${prop('theme.body.tabletWidth')}) {
    padding-left: 0px;
    width: 150px;
  }
`

const Normal = styled.strong`
  font-size: ${prop('theme.costSummary.totalCost.fontSize')};
  font-weight: 250;
  padding-left: 10px;
  display: inline-block;
  text-align: right;
`

const Link = styled.a`
  text-align: right;
  text-decoration: underline;
  color: ${theme('colors.primary')};
  font-size: ${prop('theme.costSummary.totalCost.link.fontSize')};
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
`
const ColumnItem = styled.div`
  padding-left: 104px;
  text-align: left;
  font-weight: 500;
  font-size: 16px;
  width: 330px;
  @media (max-width: ${prop('theme.body.tabletWidth')}) {
    padding-left: 0px;
    width: 150px;
  }
`

const Summary = ({
  sections,
  selection,
  onSelect,
  summary,
  months,
  handleMonths,
  monthsToPayOptions,
  setShowModal,
  financeOption,
  financeId,
  financeTitle,
  handleFinanceOption,
}) => {
  const { t, country } = useTranslation()
  const monthlyPayment = hasFullVersion
    ? getMonthlyPayment(summary, months, financeId).toFixed(2)
    : getMonthlyPaymentShortVersion(summary)
  const [elementWidth, setElementWidth] = useState()
  const selectRef = useRef()
  const { storage } = useContext(Context)

  const setWidth = () => {
    if (selectRef.current !== undefined) {
      setElementWidth(selectRef.current.clientWidth)
    }
  }

  /* eslint-disable */
  const getStorageItems = () => {
    const items = []

    for (const item of storage.cards) {
      if (item.amount > 0) {
        items.push(
          <ColumnItem>
            {item.title} {item.amount}
          </ColumnItem>
        )
      }
    }

    if (items.length === 0) {
      items.push(<ColumnItem>No storage</ColumnItem>)
      return items
    }
    return items
  }
  /* eslint-enable */

  const params = new URLSearchParams(document.location.search)
  const isInStore = params.get('instore')
  const width = window.innerWidth

  useEffect(() => {
    window.addEventListener('resize', setWidth)
    setWidth()
    return () => window.removeEventListener('resize', setWidth)
  }, [])

  const hasFinanceLink = !t('app.finance.terms-and-conditions.link').startsWith(
    'app'
  )

  return (
    <Wrapper>
      <SectionSummaries>
        {sections
          .filter((section) => section.id !== 'estimate')
          .map((section) => {
            let value = ''
            /* eslint-disable */
            const options = section.cards.map((card) => {
              const selectionItem = selection[section.id] || {}
              if (card.title === selectionItem.title) {
                if (selectionItem.id !== 'installation') {
                  value = card.title
                } else {
                  value = getInstallationText({ card, selectionItem })
                }
              }
              return <option key={`${card.title}-option`}>{card.title}</option>
            })
            /* eslint-enable */
            // Mobile-version
            if (width < 680) {
              return (
                <SectionSummary key={`${section.title}-summary`}>
                  {hasMagnetSimplifiedVersion ? (
                    <>
                      <CenteredLabel>
                        {section.shortTitle || section.title}:
                      </CenteredLabel>
                      <Row>
                        {section.id !== 'storage' ? (
                          <StrongSummery>{value}</StrongSummery>
                        ) : (
                          <Column>{getStorageItems()}</Column>
                        )}
                        {section.id !== 'range' && section.id !== 'cabinet' && (
                          <Price>
                            {getFormatedPrice({
                              card: selection[section.id],
                              selection,
                              country,
                              context: storage,
                            })}
                          </Price>
                        )}
                      </Row>
                    </>
                  ) : (
                    <>
                      <Label>{section.shortTitle || section.title}:</Label>
                      <Row>
                        <Select
                          ref={selectRef}
                          id={`select-${section.title}`}
                          backgroundUrl={t('app.components.summary.icon-url')}
                          onChange={(e) => {
                            onSelect(
                              section.cards.find(
                                (card) => card.title === e.target.value
                              )
                            )
                          }}
                          value={value}
                        >
                          {options}
                        </Select>
                        {section.id !== 'range' && section.id !== 'cabinet' && (
                          <Price>
                            {getFormatedPrice({
                              card: selection[section.id],
                              selection,
                              country,
                            })}
                          </Price>
                        )}
                      </Row>
                    </>
                  )}
                </SectionSummary>
              )
            }

            // Desktop-version
            return (
              <SectionSummary key={`${section.title}-summary`}>
                {hasMagnetSimplifiedVersion ? (
                  <Row>
                    <CenteredLabel>
                      {section.shortTitle || section.title}:
                    </CenteredLabel>
                    {section.id !== 'storage' ? (
                      <StrongSummery>{value}</StrongSummery>
                    ) : (
                      <Column>{getStorageItems()}</Column>
                    )}
                    {section.id !== 'range' && section.id !== 'cabinet' && (
                      <Price>
                        {getFormatedPrice({
                          card: selection[section.id],
                          selection,
                          country,
                          context: storage,
                        })}
                      </Price>
                    )}
                  </Row>
                ) : (
                  <>
                    <Label>{section.shortTitle || section.title}:</Label>
                    <Row>
                      <Select
                        ref={selectRef}
                        id={`select-${section.title}`}
                        backgroundUrl={t('app.components.summary.icon-url')}
                        onChange={(e) => {
                          onSelect(
                            section.cards.find(
                              (card) => card.title === e.target.value
                            )
                          )
                        }}
                        value={value}
                      >
                        {options}
                      </Select>
                      {section.id !== 'range' && section.id !== 'cabinet' && (
                        <Price>
                          {getFormatedPrice({
                            card: selection[section.id],
                            selection,
                            country,
                          })}
                        </Price>
                      )}
                    </Row>
                  </>
                )}
              </SectionSummary>
            )
          })}
        <CostWrapper>
          <CostRow>
            <Span
              hasMonthlyPayment={hasMonthlyPayment}
              hasFullVersion={hasFullVersion}
              elementWidth={elementWidth}
              hasMagnetSimplifiedVersion={hasMagnetSimplifiedVersion}
            >
              {hasMagnetSimplifiedVersion ? (
                <Total>{t('app.components.summary.title')}</Total>
              ) : (
                <P>{t('app.components.summary.title')}</P>
              )}
              <Cost>
                <Strong>{formatPrice({ price: summary, country })}</Strong>
                {hasMonthlyPayment && !hasFullVersion && (
                  <>
                    <Strong>
                      <Normal>
                        {t('app.components.cost-summary.alternative')}
                      </Normal>{' '}
                      {formatPrice({
                        price: getMonthlyPaymentShortVersion(summary),
                        country,
                      })}
                      {t('app.components.cost-summary.per-month')}
                    </Strong>
                  </>
                )}
              </Cost>
            </Span>
          </CostRow>
          <CostRow>
            <Span
              hasMonthlyPayment={hasMonthlyPayment}
              hasFullVersion={hasFullVersion}
              elementWidth={elementWidth}
            >
              <P />
              <Cost>
                {hasFinanceLink && !isInStore && (
                  <Link
                    href={t('app.finance.terms-and-conditions.link')}
                    target="_blank"
                  >
                    {t('app.finance.terms-and-conditions.description')}
                  </Link>
                )}
              </Cost>
            </Span>
          </CostRow>
          {hasMonthlyPayment && hasFullVersion && (
            <FinanceSelection
              selection={selection}
              setShowModal={setShowModal}
              summary={summary}
              financeOption={financeOption}
              financeId={financeId}
              handleFinanceOption={handleFinanceOption}
              handleMonths={handleMonths}
              monthsToPayOptions={monthsToPayOptions}
              months={months}
              elementWidth={elementWidth}
            />
          )}
        </CostWrapper>
      </SectionSummaries>
      {isInStore !== 'true' && (
        <Email
          configuration={{
            ...selection,
            summary,
            summaryMonthly: parseFloat(monthlyPayment),
          }}
          financeTitle={financeTitle}
          months={months}
          selection={selection}
        />
      )}
    </Wrapper>
  )
}
Summary.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired })
  ).isRequired,
  selection: PropTypes.shape({}).isRequired,
  onSelect: PropTypes.func.isRequired,
  summary: PropTypes.number.isRequired,
  months: PropTypes.shape({
    months: PropTypes.number.isRequired,
  }).isRequired,
  monthsToPayOptions: PropTypes.arrayOf(
    PropTypes.shape({
      repaymentFactor: PropTypes.number.isRequired,
      months: PropTypes.number.isRequired,
    })
  ).isRequired,
  financeOption: PropTypes.shape({}).isRequired,
  financeId: PropTypes.string.isRequired,
  financeTitle: PropTypes.string.isRequired,
  setShowModal: PropTypes.func.isRequired,
  handleFinanceOption: PropTypes.func.isRequired,
  handleMonths: PropTypes.func.isRequired,
}

export default Summary
